import { Checkbox, Switch } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  BoundHeader,
  Filter,
  FilterContainer,
  FilterHeader,
  FilterPrice,
  FilterTitle,
  type StopsFilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { FilterLoadingLayer } from './ChangeTransportFiltersLoading';
import type { ClickParams } from '../../search/Curiosity/Analytics/utils';
import { useTransportPageFilterTracking } from './hooks/useTransportPageFilterTracking';

type ChangeFlightStopFilterProps = {
  outboundStops?: StopsFilter[];
  inboundStops?: StopsFilter[];
  directOnly?: DirectOnlyFilter;
  onChange: (filterChange: StopsFilterChange) => void;
};

function getStopFilterChangeData(
  type: BoundType,
  bound: StopsFilter[],
  newFilter: StopsFilterValue,
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void,
  sendStratrackFlightPageFilterEvent: (payload: StartrackFilterPayload) => void
): StopsFilterChange {
  const newStateMapper = (val: StopsFilter) => (val.stops === newFilter ? { ...val, selected: !val.selected } : val);
  const origin = type === 'outbound' ? 'STOPS_OUTBOUND_FILTER' : 'STOPS_RETURN_FILTER';
  sendStratrackFlightPageFilterEvent({
    filterName: origin,
    value: newFilter,
    deselectFlag: !!bound.find((val) => val.stops === newFilter)?.selected,
  });
  sendCuriosityClickEvent({
    event_name: `click_on_${type}_stops_filters`,
    event_context: 'change_transport_page',
    event_value: newFilter,
  });
  return {
    newState: {
      ...(type === 'outbound'
        ? { outboundStops: bound.map(newStateMapper) }
        : { inboundStops: bound.map(newStateMapper) }),
    },
    event: { type: 'FILTER', origin },
  };
}

export function getStopFilterDirectRoutes(
  enabled: boolean,
  directOnly: DirectOnlyFilter,
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void,
  sendStratrackFlightPageFilterEvent: (payload: StartrackFilterPayload) => void
): StopsFilterChange {
  sendStratrackFlightPageFilterEvent({
    filterName: 'DIRECT_FLIGHTS_FILTER',
    value: enabled.toString(),
    deselectFlag: !enabled,
  });
  sendCuriosityClickEvent({
    event_name: `click_on_direct_flight_filters`,
    event_context: 'change_transport_page',
    event_value: `enabled_${enabled}`,
  });
  return {
    newState: {
      directOnly: { ...directOnly, selected: enabled },
    },
    event: { type: 'FILTER', origin: 'DIRECT_FLIGHTS_FILTER' },
  };
}

const isSelected = (val: StopsFilter) => val.selected;
const isDirect = (val: StopsFilter) => val.stops === 'NO_STOPS' && val.selected;

export function getStopFilterDirectRoutesChecked(
  directOnly: DirectOnlyFilter,
  outbound: StopsFilter[],
  inbound: StopsFilter[]
): boolean {
  return (
    directOnly.selected ||
    (outbound.filter(isSelected).length === 1 &&
      outbound.filter(isDirect).length === 1 &&
      inbound.filter(isSelected).length === 1 &&
      inbound.filter(isDirect).length === 1)
  );
}

export default function ChangeFlightStopFilter(props: ChangeFlightStopFilterProps) {
  const { outboundStops, inboundStops, directOnly, onChange } = props;
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  return (outboundStops && outboundStops.length > 1) || (inboundStops && inboundStops.length > 1) || directOnly ? (
    <Filter>
      <FilterHeader>
        <FilterTitle>{t('vacation.common.filters.stops.title')}</FilterTitle>
      </FilterHeader>
      {directOnly && (
        <FilterContainer
          css={`
            flex-direction: row;
            justify-content: space-between;
          `}
        >
          <div
            css={`
              position: relative;
              display: flex;
              gap: 8px;
              justify-content: space-between;
              width: 100%;
            `}
          >
            <span>
              {isTrain
                ? t('vacation.trainavailability.filters.directtrains')
                : t('vacation.flightavailability.filters.directflights')}
            </span>
            <Switch
              label=""
              isLabelVisible={false}
              disabled={!directOnly?.enabled}
              checked={getStopFilterDirectRoutesChecked(directOnly, outboundStops || [], inboundStops || [])}
              onChange={(enabled) =>
                onChange(
                  getStopFilterDirectRoutes(
                    enabled,
                    directOnly,
                    sendCuriosityClickEvent,
                    sendStratrackFlightPageFilterEvent
                  )
                )
              }
            />
            <FilterLoadingLayer origin="DIRECT_FLIGHTS_FILTER" />
          </div>
        </FilterContainer>
      )}
      {outboundStops && outboundStops.length > 1 && (
        <FilterContainer
          css={`
            row-gap: 8px;
            position: relative;
          `}
        >
          <BoundHeader type="outbound" />
          <div
            css={`
              position: relative;
            `}
          >
            {outboundStops.map((stop) => (
              <StopFilterCheck
                key={stop.stops}
                {...stop}
                directOnly={directOnly}
                onStopFilterChange={(value) =>
                  onChange(
                    getStopFilterChangeData(
                      'outbound',
                      outboundStops,
                      value,
                      sendCuriosityClickEvent,
                      sendStratrackFlightPageFilterEvent
                    )
                  )
                }
              />
            ))}
            <FilterLoadingLayer origin="STOPS_OUTBOUND_FILTER" />
          </div>
        </FilterContainer>
      )}
      {inboundStops && inboundStops.length > 1 && (
        <FilterContainer
          css={`
            row-gap: 8px;
            position: relative;
          `}
        >
          <BoundHeader type="inbound" />
          <div
            css={`
              position: relative;
            `}
          >
            {inboundStops.map((stop) => (
              <StopFilterCheck
                key={stop.stops}
                {...stop}
                directOnly={directOnly}
                onStopFilterChange={(value) =>
                  onChange(
                    getStopFilterChangeData(
                      'inbound',
                      inboundStops,
                      value,
                      sendCuriosityClickEvent,
                      sendStratrackFlightPageFilterEvent
                    )
                  )
                }
              />
            ))}
            <FilterLoadingLayer origin="STOPS_RETURN_FILTER" />
          </div>
        </FilterContainer>
      )}
    </Filter>
  ) : null;
}

type StopFilterCheckProps = {
  onStopFilterChange: (stops: StopsFilterValue) => void;
  directOnly?: DirectOnlyFilter;
} & StopsFilter;

function StopFilterCheck(props: StopFilterCheckProps) {
  const { enabled, selected, minPrice, stops, onStopFilterChange, directOnly } = props;
  const { t } = useTranslation();
  return (
    <div
      css={`
        display: flex;
        align-items: center;
        justify-content: space-between;
      `}
    >
      <div
        css={`
          display: flex;
          flex-direction: column;
        `}
      >
        <span>
          {stops === 'NO_STOPS'
            ? t('vacation.common.filters.stops.nostops')
            : stops === 'ONE'
              ? t('vacation.common.filters.stops.one')
              : stops === 'TWO_OR_MORE'
                ? t('vacation.common.filters.stops.twoormore')
                : 'Unknown'}
        </span>
        <FilterPrice price={enabled ? minPrice : 0} />
      </div>
      <Checkbox
        checkbox={{ id: stops }}
        disabled={!enabled || (directOnly?.selected && stops === 'NO_STOPS')}
        isChecked={selected || (directOnly?.selected && stops === 'NO_STOPS')}
        onChange={() => onStopFilterChange(stops)}
      />
    </div>
  );
}
